/**
 * OdontogramaPanel — Panel principal de la pestaña "Odontograma" del estudio.
 *
 * Solo se monta cuando el estudio es dental (cosmetico/Dental o sanitario/Odontologia).
 * Sub-pestañas:
 *   1. Configuracion: dientes incluidos, mediciones, superficies, notacion
 *   2. Estado dental: por voluntario, marcar ausentes/implantes/protesis
 *   3. Mediciones: entrada de datos por (voluntario x visita) con DentalChart
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { FloppyDisk, Plus, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import DentalChart from './DentalChart';
import DentalDiagnosisPanel from './DentalDiagnosisPanel';
import { sortByVCode } from '../../utils/sortVolunteers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// URS-F490: input con estado local en crudo para no filtrar comas mientras
// el usuario escribe. Antes el `filter(Boolean)` de options en onChange
// consumia la coma final y hacia imposible escribir mas de un item.
function MeasurementOptionsInput({ value, onChange, disabled, testid }) {
  const initial = Array.isArray(value) ? value.join(', ') : (value || '');
  const [raw, setRaw] = useState(initial);
  // Si el modelo cambia externamente y difiere del raw actual (por ejemplo
  // al recargar la config), refrescamos el input.
  useEffect(() => {
    const external = Array.isArray(value) ? value.join(', ') : (value || '');
    // Solo actualizamos si el usuario no esta editando (para no pisar comas)
    const currentParsed = raw.split(',').map(o => o.trim()).filter(Boolean).join(', ');
    if (external !== currentParsed) setRaw(external);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(value) ? value.join('|') : value]);
  return (
    <Input
      value={raw}
      onChange={e => {
        const v = e.target.value;
        setRaw(v);
        onChange(v.split(',').map(o => o.trim()).filter(Boolean));
      }}
      onBlur={() => {
        // Al perder el foco normalizamos el visible (quita coma sobrante,
        // espacios repetidos).
        const parsed = raw.split(',').map(o => o.trim()).filter(Boolean);
        setRaw(parsed.join(', '));
      }}
      placeholder="Leve, Moderado, Severo"
      disabled={disabled}
      data-testid={testid}
    />
  );
}
const DEFAULT_SURFACES = ["MV", "V", "DV", "ML", "L", "DL"];

// URS-F462: tipos de diente/prótesis a los que puede aplicar una medición.
// Coinciden con los estados posibles de `teeth_status` (DentalChart.STATUS_CYCLE).
const TOOTH_KINDS = [
  { value: 'tooth',           label: 'Diente natural' },
  { value: 'crown',           label: 'Corona' },
  { value: 'veneer',          label: 'Carilla' },
  { value: 'prosthesis',      label: 'Protesis' },
  { value: 'implant_single',  label: 'Implante Unitario' },
  { value: 'implant_multi',   label: 'Implante Multiple' },
];

// ─────────────────────────────────────────────────────────────────────────
// Sub-panel 1: CONFIG
// ─────────────────────────────────────────────────────────────────────────
function ConfigSubPanel({ studyId, config, setConfig, fdiTeeth, visits, onSave, saving, canEdit }) {
  // URS-F462: helpers para toggle en arrays multi-seleccion dentro de una medicion.
  const toggleMeasurementListItem = (idx, key, value) => {
    if (!canEdit) return;
    const arr = [...(config.measurements || [])];
    const list = Array.isArray(arr[idx][key]) ? [...arr[idx][key]] : [];
    const pos = list.indexOf(value);
    if (pos >= 0) list.splice(pos, 1);
    else list.push(value);
    arr[idx] = { ...arr[idx], [key]: list };
    setConfig({ ...config, measurements: arr });
  };
  const toggleTooth = (t) => {
    if (!canEdit) return;
    const set = new Set(config.teeth_included || []);
    if (set.has(t)) set.delete(t);
    else set.add(t);
    // Preserve FDI order
    const ordered = fdiTeeth.filter(x => set.has(x));
    setConfig({ ...config, teeth_included: ordered });
  };

  const addMeasurement = () => {
    setConfig({
      ...config,
      measurements: [...(config.measurements || []), { name: '', label: '', min_range: null, max_range: null, legend: '', index_type: null, value_type: 'number', options: [], unit: '', per_surface: null, visit_codes: [], applies_to: [] }],
    });
  };
  const updateMeasurement = (idx, patch) => {
    const arr = [...(config.measurements || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setConfig({ ...config, measurements: arr });
  };
  const removeMeasurement = (idx) => {
    const arr = [...(config.measurements || [])];
    arr.splice(idx, 1);
    setConfig({ ...config, measurements: arr });
  };

  return (
    <div className="space-y-6" data-testid="odonto-config-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium text-stone-800">Sistema de notacion</Label>
          <Select value={config.notation_system || 'FDI'} onValueChange={v => setConfig({ ...config, notation_system: v })} disabled={!canEdit}>
            <SelectTrigger data-testid="odonto-notation" className="bg-white border-stone-200 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="FDI">FDI (11-48) - Estandar Europa</SelectItem>
              <SelectItem value="Universal">Universal (1-32) - EEUU</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-medium text-stone-800">Superficies dentales</Label>
          <div className="mt-1 flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input
                type="checkbox"
                checked={!!config.use_surfaces}
                onChange={e => setConfig({ ...config, use_surfaces: e.target.checked, surfaces: e.target.checked ? (config.surfaces?.length ? config.surfaces : DEFAULT_SURFACES) : [] })}
                disabled={!canEdit}
                data-testid="odonto-use-surfaces"
              />
              Activar mediciones por superficie (MV, V, DV, ML, L, DL)
            </label>
          </div>
        </div>
      </div>

      {/* Dientes incluidos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium text-stone-800">Dientes incluidos ({(config.teeth_included || []).length}/32)</Label>
          {canEdit && (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setConfig({ ...config, teeth_included: [...fdiTeeth] })} data-testid="odonto-select-all-teeth">Todos</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setConfig({ ...config, teeth_included: [] })} data-testid="odonto-clear-teeth">Ninguno</Button>
            </div>
          )}
        </div>
        <div className="flex justify-center">
          <DentalChart
            notationSystem={config.notation_system || 'FDI'}
            teethIncluded={config.teeth_included || []}
            onSelectTooth={toggleTooth}
            selectedTooth={null}
            mode="config"
          />
        </div>
        <p className="text-xs text-stone-500 mt-1 text-center">
          Click en un diente para incluirlo o excluirlo del estudio. Solo los dientes incluidos podran medirse mas adelante.
        </p>
      </div>

      {/* Mediciones */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium text-stone-800">Mediciones ({(config.measurements || []).length})</Label>
          {canEdit && (
            <Button type="button" size="sm" onClick={addMeasurement} data-testid="odonto-add-measurement">
              <Plus size={14} className="mr-1" /> Añadir medicion
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {(config.measurements || []).map((m, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 bg-stone-50 border border-stone-200 rounded-md">
              <div>
                <Label className="text-xs text-stone-600">Codigo</Label>
                <Input value={m.name || ''} onChange={e => updateMeasurement(idx, { name: e.target.value.toUpperCase() })} placeholder="IP" disabled={!canEdit} data-testid={`odonto-measurement-name-${idx}`} />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Etiqueta</Label>
                <Input value={m.label || ''} onChange={e => updateMeasurement(idx, { label: e.target.value })} placeholder="Indice de Placa" disabled={!canEdit} />
              </div>
              {/* URS-F482: Tipo de dato de la medicion */}
              <div>
                <Label className="text-xs text-stone-600">Tipo</Label>
                <Select value={m.value_type || 'number'} onValueChange={v => updateMeasurement(idx, { value_type: v })} disabled={!canEdit}>
                  <SelectTrigger className="bg-white" data-testid={`odonto-measurement-type-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="number">Numero</SelectItem>
                    <SelectItem value="select">Seleccion</SelectItem>
                    <SelectItem value="text">Texto</SelectItem>
                    <SelectItem value="boolean">Si / No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* URS-F482: Min y Max solo aplican para value_type=number */}
              <div>
                <Label className="text-xs text-stone-600">Min</Label>
                <Input type="number" value={m.min_range ?? ''} onChange={e => updateMeasurement(idx, { min_range: e.target.value === '' ? null : Number(e.target.value) })} disabled={!canEdit || (m.value_type && m.value_type !== 'number')} placeholder={m.value_type && m.value_type !== 'number' ? '—' : ''} />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Max</Label>
                <Input type="number" value={m.max_range ?? ''} onChange={e => updateMeasurement(idx, { max_range: e.target.value === '' ? null : Number(e.target.value) })} disabled={!canEdit || (m.value_type && m.value_type !== 'number')} placeholder={m.value_type && m.value_type !== 'number' ? '—' : ''} />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Indice Löe-Silness</Label>
                <Select value={m.index_type || '_none'} onValueChange={v => updateMeasurement(idx, { index_type: v === '_none' ? null : v })} disabled={!canEdit}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-</SelectItem>
                    <SelectItem value="pi">PI (Placa)</SelectItem>
                    <SelectItem value="gi">GI (Gingival)</SelectItem>
                    <SelectItem value="ppd">PPD (Sondaje)</SelectItem>
                    <SelectItem value="bop">BOP (Sangrado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                {canEdit && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeMeasurement(idx)} data-testid={`odonto-remove-measurement-${idx}`}>
                    <X size={14} />
                  </Button>
                )}
              </div>
              {/* URS-F482: Opciones / Unidad segun tipo */}
              <div className="md:col-span-3">
                <Label className="text-xs text-stone-600">
                  {m.value_type === 'select' ? 'Opciones (separadas por coma)' :
                   m.value_type === 'number' ? 'Unidad' : 'Extra'}
                </Label>
                {m.value_type === 'select' ? (
                  <MeasurementOptionsInput
                    value={m.options}
                    onChange={(opts) => updateMeasurement(idx, { options: opts })}
                    disabled={!canEdit}
                    testid={`odonto-measurement-options-${idx}`}
                  />
                ) : m.value_type === 'number' ? (
                  <Input value={m.unit || ''} onChange={e => updateMeasurement(idx, { unit: e.target.value })} placeholder="mm, %, °C..." disabled={!canEdit} />
                ) : (
                  <Input value="" placeholder="—" disabled />
                )}
              </div>
              {/* URS-F482 / URS-F490: Por diente vs Por superficie + subset */}
              <div className="md:col-span-3">
                <Label className="text-xs text-stone-600">Granularidad</Label>
                <Select
                  value={m.per_surface === true ? 'surface' : m.per_surface === false ? 'tooth' : '_global'}
                  onValueChange={v => updateMeasurement(idx, {
                    per_surface: v === 'surface' ? true : v === 'tooth' ? false : null,
                    // Al cambiar a "por diente" limpiamos el subset. Al pasar a
                    // "por superficie" o "global" mantenemos lo guardado.
                    ...(v === 'tooth' ? { surfaces: [] } : {}),
                  })}
                  disabled={!canEdit}
                >
                  <SelectTrigger className="bg-white" data-testid={`odonto-measurement-scope-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_global">Segun config global ({config.use_surfaces ? 'por superficie' : 'por diente'})</SelectItem>
                    <SelectItem value="tooth">Por diente</SelectItem>
                    <SelectItem value="surface">Por superficie</SelectItem>
                  </SelectContent>
                </Select>
                {/* URS-F490: subset multiseleccion cuando la granularidad
                    efectiva es "por superficie" (surface o global con
                    use_surfaces). Si vacio se usan todas las globales. */}
                {(() => {
                  const effectiveSurfaces = m.per_surface === true || (m.per_surface === null && config.use_surfaces);
                  if (!effectiveSurfaces) return null;
                  const globalSurfaces = (config.surfaces || []);
                  if (!globalSurfaces.length) return (
                    <p className="text-[11px] text-amber-600 mt-1">Configura primero las superficies globales del estudio.</p>
                  );
                  const selected = new Set(m.surfaces || []);
                  const allSel = selected.size === 0 || selected.size === globalSurfaces.length;
                  return (
                    <div className="mt-1.5" data-testid={`odonto-measurement-surfaces-${idx}`}>
                      <p className="text-[10px] text-stone-500 mb-1">
                        Superficies a medir {allSel ? '(todas)' : `(${selected.size}/${globalSurfaces.length})`}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {globalSurfaces.map(s => {
                          const isSel = selected.size === 0 ? true : selected.has(s);
                          return (
                            <button
                              key={s}
                              type="button"
                              disabled={!canEdit}
                              onClick={() => {
                                // Si estaba en "todas" (array vacio),
                                // pasar a "todas menos la que se destoggle".
                                let next;
                                if (selected.size === 0) {
                                  next = globalSurfaces.filter(x => x !== s);
                                } else if (selected.has(s)) {
                                  next = Array.from(selected).filter(x => x !== s);
                                } else {
                                  next = [...selected, s];
                                }
                                // Si acaban seleccionadas todas -> guardar array vacio
                                // (= "todas") para evitar drift al anadir superficies globales.
                                if (next.length === globalSurfaces.length) next = [];
                                updateMeasurement(idx, { surfaces: next });
                              }}
                              data-testid={`odonto-measurement-${idx}-surface-${s}`}
                              className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${
                                isSel
                                  ? 'bg-[#1f3e80] text-white border-[#1f3e80]'
                                  : 'bg-white text-stone-600 border-stone-300 hover:border-[#1f3e80]'
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="md:col-span-6">
                <Label className="text-xs text-stone-600">Leyenda / escala</Label>
                <Input value={m.legend || ''} onChange={e => updateMeasurement(idx, { legend: e.target.value })} placeholder="0=Sin placa, 1=Fina..." disabled={!canEdit} />
              </div>
              {/* URS-F462: Visitas del estudio en las que se recoge esta medicion */}
              <div className="md:col-span-3">
                <Label className="text-xs text-stone-600">Recoger en visitas</Label>
                <div className="flex flex-wrap gap-1 mt-1" data-testid={`odonto-measurement-visits-${idx}`}>
                  {(visits || []).length === 0 && (
                    <span className="text-[11px] text-stone-400 italic">
                      Guarda primero las visitas del protocolo para poder seleccionarlas
                    </span>
                  )}
                  {(visits || []).filter(v => v.code && !['VAL','EA'].includes(v.code)).map(v => {
                    const sel = Array.isArray(m.visit_codes) && m.visit_codes.includes(v.code);
                    return (
                      <button
                        key={v.code}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => toggleMeasurementListItem(idx, 'visit_codes', v.code)}
                        data-testid={`odonto-measurement-${idx}-visit-${v.code}`}
                        className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${
                          sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]'
                              : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                        } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {v.code}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-stone-400 mt-1">Vacio = se recoge en todas las visitas del protocolo</p>
              </div>
              {/* URS-F462: Tipos de diente a los que aplica esta medicion */}
              <div className="md:col-span-3">
                <Label className="text-xs text-stone-600">Aplica a</Label>
                <div className="flex flex-wrap gap-1 mt-1" data-testid={`odonto-measurement-applies-${idx}`}>
                  {TOOTH_KINDS.map(k => {
                    const sel = Array.isArray(m.applies_to) && m.applies_to.includes(k.value);
                    return (
                      <button
                        key={k.value}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => toggleMeasurementListItem(idx, 'applies_to', k.value)}
                        data-testid={`odonto-measurement-${idx}-applies-${k.value}`}
                        className={`px-2 py-0.5 rounded text-[11px] border transition-colors ${
                          sel ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                        } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {k.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-stone-400 mt-1">Vacio = aplica a todos los tipos configurados</p>
              </div>
            </div>
          ))}
          {(config.measurements || []).length === 0 && (
            <p className="text-sm text-stone-500 italic">No hay mediciones configuradas.</p>
          )}
        </div>
      </div>

      {/* URS-F382: Parametros adicionales */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium text-stone-800">Parametros adicionales ({(config.parameters || []).length})</Label>
          {canEdit && (
            <Button type="button" size="sm" onClick={() => setConfig({ ...config, parameters: [...(config.parameters || []), { name: '', label: '', scope: 'patient', param_type: 'boolean', options: [], unit: '' }] })} data-testid="odonto-add-parameter">
              <Plus size={14} className="mr-1" /> Añadir parametro
            </Button>
          )}
        </div>
        <p className="text-xs text-stone-500 mb-2">
          Variables extra por voluntario (fumador, IMC...) o por visita (halitosis, VAS dolor...). Se guardan aparte de las mediciones por diente.
        </p>
        <div className="space-y-2">
          {(config.parameters || []).map((p, idx) => {
            const updateP = (patch) => {
              const arr = [...(config.parameters || [])];
              arr[idx] = { ...arr[idx], ...patch };
              setConfig({ ...config, parameters: arr });
            };
            const removeP = () => {
              const arr = [...(config.parameters || [])];
              arr.splice(idx, 1);
              setConfig({ ...config, parameters: arr });
            };
            return (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 bg-stone-50 border border-stone-200 rounded-md">
                <div>
                  <Label className="text-xs text-stone-600">Codigo</Label>
                  <Input value={p.name || ''} onChange={e => updateP({ name: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="supuracion" disabled={!canEdit} data-testid={`odonto-param-name-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Etiqueta</Label>
                  <Input value={p.label || ''} onChange={e => updateP({ label: e.target.value })} placeholder="Supuracion" disabled={!canEdit} />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Scope</Label>
                  <Select value={p.scope || 'patient'} onValueChange={v => updateP({ scope: v })} disabled={!canEdit}>
                    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="patient">Por voluntario</SelectItem>
                      <SelectItem value="measurement">Por visita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Tipo</Label>
                  <Select value={p.param_type || 'boolean'} onValueChange={v => updateP({ param_type: v })} disabled={!canEdit}>
                    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boolean">Si/No</SelectItem>
                      <SelectItem value="number">Numero</SelectItem>
                      <SelectItem value="text">Texto</SelectItem>
                      <SelectItem value="select">Lista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-stone-600">{p.param_type === 'number' ? 'Unidad' : 'Opciones (coma)'}</Label>
                  {p.param_type === 'select' ? (
                    <Input value={(p.options || []).join(', ')} onChange={e => updateP({ options: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} placeholder="Leve, Moderado, Severo" disabled={!canEdit} />
                  ) : (
                    <Input value={p.unit || ''} onChange={e => updateP({ unit: e.target.value })} placeholder={p.param_type === 'number' ? 'mm, %, °C...' : ''} disabled={!canEdit || p.param_type !== 'number'} />
                  )}
                </div>
                <div className="flex items-end">
                  {canEdit && (
                    <Button type="button" size="sm" variant="ghost" onClick={removeP} data-testid={`odonto-remove-parameter-${idx}`}>
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {(config.parameters || []).length === 0 && (
            <p className="text-sm text-stone-500 italic">No hay parametros adicionales configurados.</p>
          )}
        </div>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={onSave} disabled={saving} data-testid="odonto-save-config">
            <FloppyDisk size={16} className="mr-1" />
            {saving ? 'Guardando...' : 'Guardar configuracion'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-panel 2: ESTADO DENTAL
// ─────────────────────────────────────────────────────────────────────────
function TeethStatusSubPanel({ studyId, volunteers, config, canEdit }) {
  const [selectedVol, setSelectedVol] = useState(null);
  const [teethStatus, setTeethStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedVol) { setTeethStatus({}); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${selectedVol}/teeth-status`, { withCredentials: true })
      .then(r => setTeethStatus(r.data?.teeth_status || {}))
      .catch(() => toast.error('Error cargando estado dental'))
      .finally(() => setLoading(false));
  }, [selectedVol, studyId]);

  const handleChangeStatus = (tooth, newStatus) => {
    setTeethStatus(prev => ({ ...prev, [tooth]: newStatus }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/studies/${studyId}/volunteers/${selectedVol}/teeth-status`,
        { teeth_status: teethStatus },
        { withCredentials: true },
      );
      toast.success('Estado dental guardado');
    } catch (e) {
      toast.error(errText(e, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="odonto-teeth-status-panel">
      {(volunteers || []).length === 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800" data-testid="odonto-no-included-volunteers">
          No hay voluntarios en estado <b>&quot;incluido&quot;</b> o <b>&quot;finalizado&quot;</b> en este estudio. Solo estos voluntarios pueden tener mediciones dentales registradas.
        </div>
      )}
      <div className="flex items-end gap-3">
        <div className="flex-1 max-w-md">
          <Label className="text-sm font-medium text-stone-800">Voluntario</Label>
          <Select value={selectedVol || ''} onValueChange={setSelectedVol}>
            <SelectTrigger data-testid="odonto-volunteer-select" className="bg-white border-stone-200 mt-1">
              <SelectValue placeholder="Seleccionar voluntario..." />
            </SelectTrigger>
            <SelectContent>
              {(volunteers || []).map(v => (
                <SelectItem key={v.volunteer_id} value={v.volunteer_id}>
                  {v.study_volunteer_code || v.volunteer_id} — {v.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedVol && canEdit && (
          <Button onClick={handleSave} disabled={saving} data-testid="odonto-save-teeth-status">
            <FloppyDisk size={16} className="mr-1" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        )}
      </div>

      {selectedVol && !loading && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-stone-600">
            Click en un diente para ciclar entre: Normal → Ausente → Protesis → Implante Unitario → Implante Multiple → Corona → Carilla
          </p>
          <DentalChart
            notationSystem={config.notation_system || 'FDI'}
            teethIncluded={config.teeth_included || []}
            teethStatus={teethStatus}
            onChangeStatus={handleChangeStatus}
            mode="status"
          />
        </div>
      )}
      {!selectedVol && (
        <p className="text-sm text-stone-500 italic">Selecciona un voluntario para configurar su estado dental inicial.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-panel 3: MEDICIONES
// ─────────────────────────────────────────────────────────────────────────
function MeasurementsSubPanel({ studyId, volunteers, visits, config, canEdit }) {
  const [selectedVol, setSelectedVol] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [teethStatus, setTeethStatus] = useState({});
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [measurementsMap, setMeasurementsMap] = useState({}); // key = tooth|measurement|surface → { value, id, existing }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [correctionDialog, setCorrectionDialog] = useState(null); // { measurementId, oldValue, newValue }

  // Load data when (vol, visit) changes
  useEffect(() => {
    if (!selectedVol || !selectedVisit) { return; }
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${selectedVol}/teeth-status`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/dental-measurements?volunteer_id=${selectedVol}&visit_id=${selectedVisit}`, { withCredentials: true }),
    ]).then(([ts, meas]) => {
      setTeethStatus(ts.data?.teeth_status || {});
      const mp = {};
      (meas.data?.items || []).forEach(m => {
        const key = `${m.tooth_number}|${m.measurement_name}|${m.surface || ''}`;
        mp[key] = { value: m.value, id: m.id, existing: true, dirty: false };
      });
      setMeasurementsMap(mp);
    }).catch(() => toast.error('Error cargando datos'))
      .finally(() => setLoading(false));
  }, [selectedVol, selectedVisit, studyId]);

  const surfaces = useMemo(() => {
    return config.use_surfaces ? (config.surfaces || DEFAULT_SURFACES) : [null];
  }, [config]);

  const measurements = config.measurements || [];

  // Compute completed teeth (all cells filled)
  const completedTeeth = useMemo(() => {
    const set = new Set();
    const activeTeeth = (config.teeth_included || []).filter(t => teethStatus[t] !== 'missing');
    activeTeeth.forEach(t => {
      let allFilled = true;
      for (const m of measurements) {
        // URS-F482: granularidad efectiva por medición
        const effUseSurfaces = m.per_surface === true
          ? true
          : m.per_surface === false
            ? false
            : !!config.use_surfaces;
        // URS-F490: subset por medicion (si esta definido, solo se
        // consideran esas superficies como obligatorias).
        const globalSurfaces = (config.surfaces || DEFAULT_SURFACES);
        const measurementSubset = (m.surfaces || []).filter(s => globalSurfaces.includes(s));
        const effSurfaces = effUseSurfaces
          ? (measurementSubset.length ? measurementSubset : globalSurfaces)
          : [null];
        for (const s of effSurfaces) {
          const k = `${t}|${m.name}|${s || ''}`;
          if (!measurementsMap[k] || measurementsMap[k].value === '' || measurementsMap[k].value == null) {
            allFilled = false;
            break;
          }
        }
        if (!allFilled) break;
      }
      if (allFilled) set.add(t);
    });
    return set;
  }, [measurementsMap, config, teethStatus, measurements]);

  const handleCellChange = (tooth, measurement, surface, newValue) => {
    const key = `${tooth}|${measurement}|${surface || ''}`;
    setMeasurementsMap(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), value: newValue, dirty: true },
    }));
  };

  const validateRange = (measurement, value) => {
    if (value === '' || value == null) return null;
    const mc = measurements.find(x => x.name === measurement);
    if (!mc) return null;
    // URS-F482: solo validar rango si el tipo es number (o no definido, legacy)
    if (mc.value_type && mc.value_type !== 'number') return null;
    const n = Number(value);
    if (isNaN(n)) return null;
    if (mc.min_range != null && n < mc.min_range) return `< min ${mc.min_range}`;
    if (mc.max_range != null && n > mc.max_range) return `> max ${mc.max_range}`;
    return null;
  };

  const handleSave = async () => {
    if (!selectedVol || !selectedVisit) return;
    // Filter dirty NEW cells (not existing, but with value) → bulk save
    const toCreate = [];
    const toCorrect = [];
    Object.entries(measurementsMap).forEach(([key, val]) => {
      if (!val.dirty) return;
      if (val.value === '' || val.value == null) return;
      const [tooth, mname, surface] = key.split('|');
      if (val.existing) {
        toCorrect.push({ id: val.id, tooth, mname, surface: surface || null, value: val.value });
      } else {
        toCreate.push({
          tooth_number: tooth,
          measurement_name: mname,
          value: String(val.value),
          surface: surface || null,
        });
      }
    });

    if (toCorrect.length > 0) {
      toast.error(`Hay ${toCorrect.length} valores existentes modificados. Usa la funcion "Corregir" (clic derecho o boton) con firma electronica para cada uno.`);
      return;
    }

    if (toCreate.length === 0) {
      toast.info('No hay cambios que guardar.');
      return;
    }

    setSaving(true);
    try {
      const url = `${API_URL}/api/studies/${studyId}/dental-measurements/bulk`;
      const r = await axios.post(
        url,
        { volunteer_id: selectedVol, visit_id: selectedVisit, measurements: toCreate },
        { withCredentials: true },
      );
      toast.success(`Guardado: ${r.data.created} mediciones (${r.data.skipped || 0} sin cambios)`);
      // Reload
      const meas = await axios.get(
        `${API_URL}/api/studies/${studyId}/dental-measurements?volunteer_id=${selectedVol}&visit_id=${selectedVisit}`,
        { withCredentials: true },
      );
      const mp = {};
      (meas.data?.items || []).forEach(m => {
        const key = `${m.tooth_number}|${m.measurement_name}|${m.surface || ''}`;
        mp[key] = { value: m.value, id: m.id, existing: true, dirty: false };
      });
      setMeasurementsMap(mp);
    } catch (e) {
      toast.error(errText(e, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  // Correction dialog logic
  const openCorrection = (measurementId, tooth, mname, surface, oldValue) => {
    setCorrectionDialog({ measurementId, tooth, mname, surface, oldValue, newValue: oldValue, reason: '', pin: '' });
  };

  const submitCorrection = async () => {
    const { measurementId, newValue, reason, pin, tooth, mname, surface } = correctionDialog;
    if (!reason.trim() || !pin.trim()) {
      toast.error('Motivo y PIN son obligatorios');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/dental-measurements/${measurementId}/correct`,
        { value: String(newValue), reason: reason.trim(), pin: pin.trim() },
        { withCredentials: true },
      );
      toast.success('Correccion firmada y registrada');
      setCorrectionDialog(null);
      // Reload
      const meas = await axios.get(
        `${API_URL}/api/studies/${studyId}/dental-measurements?volunteer_id=${selectedVol}&visit_id=${selectedVisit}`,
        { withCredentials: true },
      );
      const mp = {};
      (meas.data?.items || []).forEach(m => {
        const key = `${m.tooth_number}|${m.measurement_name}|${m.surface || ''}`;
        mp[key] = { value: m.value, id: m.id, existing: true, dirty: false };
      });
      setMeasurementsMap(mp);
    } catch (e) {
      toast.error(errText(e, 'Error al firmar correccion'));
    }
  };

  return (
    <div className="space-y-4" data-testid="odonto-measurements-panel">
      {(volunteers || []).length === 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800" data-testid="odonto-meas-no-included-volunteers">
          No hay voluntarios en estado <b>&quot;incluido&quot;</b> o <b>&quot;finalizado&quot;</b> en este estudio. Solo estos voluntarios pueden tener mediciones dentales registradas.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-sm font-medium text-stone-800">Voluntario</Label>
          <Select value={selectedVol || ''} onValueChange={setSelectedVol}>
            <SelectTrigger data-testid="odonto-meas-volunteer" className="bg-white border-stone-200 mt-1">
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {(volunteers || []).map(v => (
                <SelectItem key={v.volunteer_id} value={v.volunteer_id}>
                  {v.study_volunteer_code || v.volunteer_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-medium text-stone-800">Visita</Label>
          <Select value={selectedVisit || ''} onValueChange={setSelectedVisit} disabled={!selectedVol}>
            <SelectTrigger data-testid="odonto-meas-visit" className="bg-white border-stone-200 mt-1">
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {(visits || []).map(v => (
                <SelectItem key={v.visit_id} value={v.visit_id}>
                  {v.code} — {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          {selectedVol && selectedVisit && canEdit && (
            <Button onClick={handleSave} disabled={saving || loading} data-testid="odonto-save-measurements">
              <FloppyDisk size={16} className="mr-1" />
              {saving ? 'Guardando...' : 'Guardar mediciones'}
            </Button>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-stone-500">Cargando...</p>}

      {selectedVol && selectedVisit && !loading && (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium text-stone-800 mb-2 text-center">Selecciona un diente</h4>
            <div className="flex justify-center">
              <DentalChart
                notationSystem={config.notation_system || 'FDI'}
                teethIncluded={config.teeth_included || []}
                teethStatus={teethStatus}
                selectedTooth={selectedTooth}
                completedTeeth={completedTeeth}
                onSelectTooth={setSelectedTooth}
              />
            </div>
          </div>

          <div>
            {selectedTooth ? (
              <div>
                <h4 className="text-sm font-medium text-stone-800 mb-2">
                  Diente <span className="font-mono text-lg text-[#1f3e80]">{selectedTooth}</span>
                  {teethStatus[selectedTooth] && teethStatus[selectedTooth] !== 'normal' && (
                    <span className="ml-2 text-xs text-orange-600 uppercase">({teethStatus[selectedTooth]})</span>
                  )}
                </h4>

                {teethStatus[selectedTooth] === 'missing' ? (
                  <p className="text-sm text-stone-500 italic p-4 bg-stone-50 rounded-md">
                    Este diente esta marcado como ausente. No se pueden registrar mediciones.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {measurements.map(m => {
                      // URS-F482: granularidad por medición
                      const effUseSurfaces = m.per_surface === true
                        ? true
                        : m.per_surface === false
                          ? false
                          : !!config.use_surfaces;
                      // URS-F490: subset por medicion
                      const globalSurfaces = (config.surfaces || DEFAULT_SURFACES);
                      const measurementSubset = (m.surfaces || []).filter(s => globalSurfaces.includes(s));
                      const effSurfaces = effUseSurfaces
                        ? (measurementSubset.length ? measurementSubset : globalSurfaces)
                        : [null];
                      const vtype = m.value_type || 'number';
                      return (
                      <div key={m.name} className="p-3 bg-stone-50 rounded-md border border-stone-200">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-semibold text-sm text-stone-800">{m.name}</span>
                            <span className="text-xs text-stone-500 ml-2">{m.label}</span>
                            {m.unit && vtype === 'number' && (
                              <span className="text-[11px] text-stone-400 ml-1">({m.unit})</span>
                            )}
                          </div>
                          {m.legend && <span className="text-xs text-stone-500 italic max-w-xs truncate" title={m.legend}>{m.legend}</span>}
                        </div>
                        <div className={`grid gap-2 ${effUseSurfaces ? 'grid-cols-3' : 'grid-cols-1'}`}>
                          {effSurfaces.map(s => {
                            const key = `${selectedTooth}|${m.name}|${s || ''}`;
                            const cell = measurementsMap[key] || {};
                            const warn = validateRange(m.name, cell.value);
                            const testId = `odonto-cell-${selectedTooth}-${m.name}${s ? '-' + s : ''}`;
                            return (
                              <div key={s || 'single'}>
                                {effUseSurfaces && <Label className="text-xs text-stone-600">{s}</Label>}
                                <div className="flex items-center gap-1">
                                  {vtype === 'select' ? (
                                    <Select
                                      value={cell.value ?? ''}
                                      onValueChange={v => handleCellChange(selectedTooth, m.name, s, v)}
                                      disabled={!canEdit}
                                    >
                                      <SelectTrigger
                                        className={`bg-white ${cell.existing ? 'bg-emerald-50' : ''}`}
                                        data-testid={testId}
                                      >
                                        <SelectValue placeholder="—" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {(m.options || []).map(opt => (
                                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : vtype === 'boolean' ? (
                                    <Select
                                      value={cell.value ?? ''}
                                      onValueChange={v => handleCellChange(selectedTooth, m.name, s, v)}
                                      disabled={!canEdit}
                                    >
                                      <SelectTrigger
                                        className={`bg-white ${cell.existing ? 'bg-emerald-50' : ''}`}
                                        data-testid={testId}
                                      >
                                        <SelectValue placeholder="—" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="si">Sí</SelectItem>
                                        <SelectItem value="no">No</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Input
                                      type={vtype === 'number' ? 'number' : 'text'}
                                      inputMode={vtype === 'number' ? 'decimal' : undefined}
                                      value={cell.value ?? ''}
                                      onChange={e => handleCellChange(selectedTooth, m.name, s, e.target.value)}
                                      disabled={!canEdit}
                                      className={`bg-white ${warn ? 'border-yellow-500' : ''} ${cell.existing ? 'bg-emerald-50' : ''}`}
                                      data-testid={testId}
                                    />
                                  )}
                                  {cell.existing && canEdit && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs"
                                      onClick={() => openCorrection(cell.id, selectedTooth, m.name, s, cell.value)}
                                      data-testid={`odonto-correct-${selectedTooth}-${m.name}${s ? '-' + s : ''}`}
                                      title="Corregir (firma electronica)"
                                    >
                                      ✎
                                    </Button>
                                  )}
                                </div>
                                {warn && <p className="text-xs text-yellow-700">{warn}</p>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-stone-500 italic p-4 bg-stone-50 rounded-md text-center">
                Selecciona un diente del diagrama para ver/registrar sus mediciones.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Correction Dialog */}
      <Dialog open={!!correctionDialog} onOpenChange={o => !o && setCorrectionDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Corregir medicion — Firma electronica</DialogTitle>
          </DialogHeader>
          {correctionDialog && (
            <div className="space-y-3">
              <p className="text-sm text-stone-700">
                Diente <b>{correctionDialog.tooth}</b> — {correctionDialog.mname}
                {correctionDialog.surface ? ` (${correctionDialog.surface})` : ''}
              </p>
              <div>
                <Label className="text-xs">Valor actual (se conservara como version anterior)</Label>
                <Input value={correctionDialog.oldValue} disabled className="bg-stone-100" />
              </div>
              <div>
                <Label className="text-xs">Nuevo valor</Label>
                <Input
                  value={correctionDialog.newValue}
                  onChange={e => setCorrectionDialog({ ...correctionDialog, newValue: e.target.value })}
                  data-testid="odonto-correction-new-value"
                />
              </div>
              <div>
                <Label className="text-xs">Motivo de la correccion *</Label>
                <Textarea
                  value={correctionDialog.reason}
                  onChange={e => setCorrectionDialog({ ...correctionDialog, reason: e.target.value })}
                  placeholder="Ej: Error de transcripcion..."
                  data-testid="odonto-correction-reason"
                />
              </div>
              <div>
                <Label className="text-xs">PIN de firma *</Label>
                <Input
                  type="password"
                  value={correctionDialog.pin}
                  onChange={e => setCorrectionDialog({ ...correctionDialog, pin: e.target.value })}
                  placeholder="4-6 digitos"
                  data-testid="odonto-correction-pin"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionDialog(null)}>Cancelar</Button>
            <Button onClick={submitCorrection} data-testid="odonto-correction-submit">
              Firmar y corregir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────
export default function OdontogramaPanel({ studyId, canEdit = true, isAdmin = false }) {
  const [config, setConfig] = useState({ notation_system: 'FDI', teeth_included: [], measurements: [], use_surfaces: false, surfaces: [] });
  const [fdiTeeth, setFdiTeeth] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgR, volR, visitR] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/dental-config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      ]);
      setConfig(cfgR.data?.dental_config || {});
      setFdiTeeth(cfgR.data?.fdi_teeth || []);
      // URS-F378: se pueden medir voluntarios en estado "incluido". Ademas,
      // se mantienen editables los que ya han terminado el estudio ("completado",
      // etiqueta "Finalizado") para poder seguir cargando datos del odontograma
      // tras la ultima cita.
      const allVols = volR.data || [];
      // URS-F506: orden natural V1, V2, ..., V10, V11 (antes se ordenaba
      // lexicograficamente y salia V1, V10, V11, V2, V21...).
      setVolunteers(sortByVCode(allVols.filter(v => ['incluido', 'completado'].includes((v.status || '').toLowerCase()))));
      setVisits(visitR.data || []);
    } catch (e) {
      toast.error(errText(e, 'Error cargando datos dentales'));
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/studies/${studyId}/dental-config`,
        config,
        { withCredentials: true },
      );
      toast.success('Configuracion guardada');
    } catch (e) {
      toast.error(errText(e, 'Error al guardar configuracion'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-stone-500">Cargando modulo dental...</div>;

  return (
    <div className="space-y-4" data-testid="odontograma-panel">
      <Tabs defaultValue="config" className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="config" data-testid="odonto-subtab-config">Configuracion</TabsTrigger>
          <TabsTrigger value="status" data-testid="odonto-subtab-status">Estado dental</TabsTrigger>
          <TabsTrigger value="measurements" data-testid="odonto-subtab-measurements">Mediciones</TabsTrigger>
          {isAdmin && <TabsTrigger value="diagnosis" data-testid="odonto-subtab-diagnosis">Diagnostico</TabsTrigger>}
        </TabsList>
        <TabsContent value="config" className="mt-4">
          <ConfigSubPanel
            studyId={studyId}
            config={config}
            setConfig={setConfig}
            fdiTeeth={fdiTeeth}
            visits={visits}
            onSave={saveConfig}
            saving={saving}
            canEdit={canEdit}
          />
        </TabsContent>
        <TabsContent value="status" className="mt-4">
          <TeethStatusSubPanel
            studyId={studyId}
            volunteers={volunteers}
            config={config}
            canEdit={canEdit}
          />
        </TabsContent>
        <TabsContent value="measurements" className="mt-4">
          <MeasurementsSubPanel
            studyId={studyId}
            volunteers={volunteers}
            visits={visits}
            config={config}
            canEdit={canEdit}
          />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="diagnosis" className="mt-4">
            <DentalDiagnosisPanel studyId={studyId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
